const ACCESS_TOKEN_KEY = 'auth:accessToken';
const REFRESH_TOKEN_KEY = 'auth:refreshToken';
const REMEMBERED_EMAIL_KEY = 'auth:rememberedEmail';
const REMEMBER_ME_KEY = 'auth:rememberMe';

/**
 * authStorage is a simple wrapper around localStorage
 * for storing and retrieving authentication tokens.
 *
 * It uses namespaced keys to avoid collisions.
 */
export const authStorage = {
  /**
   * Retrieve the access token from localStorage.
   * Returns null if called on the server or if not found.
   */
  getAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  /**
   * Save a new access token in localStorage.
   * Safe to call only in a browser environment.
   *
   * @param token - The access token to store.
   */
  setAccessToken(token: string) {
    if (typeof window === 'undefined') return;
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  },

  /**
   * Retrieve the remembered email from localStorage.
   * Returns null if called on the server or if not found.
   */
  getRememberedEmail(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(REMEMBERED_EMAIL_KEY);
  },

  /**
   * Save the remembered email in localStorage.
   * Safe to call only in a browser environment.
   *
   * @param email - The email to store.
   */
  setRememberedEmail(email: string) {
    if (typeof window === 'undefined') return;
    localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
  },

  /**
   * Retrieve the remember me preference from localStorage.
   * Returns false if called on the server or if not found.
   */
  getRememberMe(): boolean {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  },

  /**
   * Save the remember me preference in localStorage.
   * Safe to call only in a browser environment.
   *
   * @param rememberMe - Whether to remember the user.
   */
  setRememberMe(rememberMe: boolean) {
    if (typeof window === 'undefined') return;
    localStorage.setItem(REMEMBER_ME_KEY, rememberMe.toString());
  },

  /**
   * Clear both access and refresh tokens from localStorage.
   */
  clearTokens() {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  /**
   * Clear remembered email and remember me preference from localStorage.
   */
  clearRememberedData() {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);
  },
};
